import { useEffect, useState, useCallback, useRef } from "react";
import { BrowserRouter, useLocation, useMatch, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import CrawlId, { shortId } from "@/components/CrawlId";
import ControlRoom from "@/components/ControlRoom";
import DatasetStudio from "@/components/DatasetStudio";
import Synthesizer from "@/components/Synthesizer";
import SettingsPanel from "@/components/SettingsPanel";
import FileVault from "@/components/FileVault";
import TrainGuide from "@/components/TrainGuide";
import UsagePanel from "@/components/UsagePanel";
import DesktopPanel from "@/components/DesktopPanel";
import QuotaBanner from "@/components/QuotaBanner";
import LoginScreen from "@/components/LoginScreen";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Flame, Database, Cpu, Sliders, Radar, Trash2, CircleDot, HardDrive, Loader2, LogOut, Gauge, MonitorDown, Search, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { id: "control", label: "Control Room", icon: Radar, testid: "nav-control-room-tab" },
  { id: "dataset", label: "Dataset Studio", icon: Database, testid: "nav-dataset-studio-tab" },
  { id: "files", label: "Files & Storage", icon: HardDrive, testid: "nav-files-tab" },
  { id: "train", label: "Train Your Model", icon: Flame, testid: "nav-train-tab" },
  { id: "synth", label: "Synthesizer", icon: Cpu, testid: "nav-llm-synth-tab" },
  { id: "usage", label: "Usage & Plan", icon: Gauge, testid: "nav-usage-tab" },
  { id: "desktop", label: "Desktop App", icon: MonitorDown, testid: "nav-desktop-tab" },
  { id: "settings", label: "Settings", icon: Sliders, testid: "nav-settings-tab" },
];

const STATUS_COLOR = {
  running: "text-cyan-400 border-cyan-500/60 bg-cyan-500/10",
  completed: "text-emerald-400 border-emerald-500/60 bg-emerald-500/10",
  paused: "text-amber-400 border-amber-500/60 bg-amber-500/10",
  stopped: "text-rose-400 border-rose-500/60 bg-rose-500/10",
  failed: "text-rose-400 border-rose-500/60 bg-rose-500/10",
  idle: "text-slate-400 border-slate-600 bg-slate-700/20",
};

function Workspace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // The selected crawl lives in the URL (/crawls/<id>) so it can be linked.
  const match = useMatch("/crawls/:crawlId");
  const urlId = match?.params?.crawlId || null;
  const [tab, setTab] = useState("control");
  const [crawls, setCrawls] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const checkedIds = useRef(new Set());

  const refresh = useCallback(async () => {
    const data = await api.listCrawls();
    setCrawls(data);
    setLoaded(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selectCrawl = useCallback((id, { replace = false } = {}) => {
    // Keep ?query params (e.g. ?preset= share links) when only the crawl changes.
    navigate({ pathname: id ? `/crawls/${id}` : "/", search: location.search }, { replace });
  }, [navigate, location.search]);

  // "/" selects the newest crawl; an ID that isn't in the list is checked
  // against the API first -- a crawl launched a moment ago can be in the URL
  // before the list refreshes -- and only then reported as not found.
  useEffect(() => {
    if (!loaded) return;
    if (!urlId) {
      if (crawls[0]) selectCrawl(crawls[0].id, { replace: true });
      return;
    }
    if (crawls.some((c) => c.id === urlId) || checkedIds.current.has(urlId)) return;
    checkedIds.current.add(urlId);
    api.getCrawl(urlId)
      .then(() => refresh())
      .catch(() => {
        toast.error(`Crawl ${shortId(urlId)}… not found`);
        selectCrawl(crawls[0]?.id, { replace: true });
      });
  }, [loaded, urlId, crawls, selectCrawl, refresh]);

  const anyRunning = crawls.some((c) => c.status === "running");
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [anyRunning, refresh]);

  const activeCrawl = crawls.find((c) => c.id === urlId) || null;
  const activeId = activeCrawl?.id || null;

  const q = query.trim().toLowerCase();
  const visibleCrawls = q
    ? crawls.filter((c) => String(c.id).toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q))
    : crawls;

  const onDelete = async (c, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete crawl "${c.name}" (${shortId(c.id)})? This can't be undone.`)) return;
    await api.deleteCrawl(c.id);
    if (activeId === c.id) selectCrawl(null, { replace: true });
    refresh();
  };

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const sidebar = (
    <Sidebar
      user={user} logout={logout} tab={tab}
      setTab={(t) => { setTab(t); setNavOpen(false); }}
      crawls={visibleCrawls} total={crawls.length} query={query} setQuery={setQuery}
      activeId={activeId}
      onSelect={(id) => { selectCrawl(id); setNavOpen(false); }}
      onDelete={onDelete}
    />
  );

  return (
    <div className="App min-h-screen bg-background text-foreground flex">
      <Toaster position="top-right" theme="dark" richColors />
      {/* Sidebar: a column from md up, a slide-out drawer on phones */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-[#0B0F18]/90 backdrop-blur-xl flex-col h-screen sticky top-0">
        {sidebar}
      </aside>
      {navOpen && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-black/60" onClick={() => setNavOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] border-r border-border bg-[#0B0F18] flex flex-col shadow-2xl">
            <button onClick={() => setNavOpen(false)} aria-label="Close menu" data-testid="mobile-nav-close"
              className="absolute right-3 top-3 z-10 p-1.5 rounded-md text-slate-400 hover:text-slate-100">
              <X className="h-4 w-4" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 grid-bg">
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-[#0B0F18]/95 backdrop-blur px-4 py-3">
          <button onClick={() => setNavOpen(true)} aria-label="Open menu" data-testid="mobile-nav-open"
            className="-ml-1 p-1.5 rounded-md text-slate-300 hover:bg-slate-800/60">
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-display font-extrabold tracking-tight">DATAFORGE <span className="text-cyan-400">AI</span></span>
          {activeCrawl && (
            <span className="ml-auto min-w-0 truncate font-mono text-[11px] text-slate-500" data-testid="mobile-active-crawl">
              {shortId(activeCrawl.id)}
            </span>
          )}
        </div>
        <QuotaBanner onOpenUsage={() => setTab("usage")} />
        {tab === "control" && (
          <ControlRoom
            crawls={crawls}
            activeCrawl={activeCrawl}
            setActiveId={selectCrawl}
            refresh={refresh}
          />
        )}
        {tab === "dataset" && <DatasetStudio activeCrawl={activeCrawl} />}
        {tab === "files" && <FileVault />}
        {tab === "train" && <TrainGuide />}
        {tab === "synth" && <Synthesizer activeCrawl={activeCrawl} />}
        {tab === "usage" && <UsagePanel />}
        {tab === "desktop" && <DesktopPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

function Sidebar({ user, logout, tab, setTab, crawls, total, query, setQuery, activeId, onSelect, onDelete }) {
  return (
    <>
        <div className="px-5 py-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-cyan-500/15 border border-cyan-500/40 grid place-items-center glow-cyan">
              <Flame className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="font-display font-extrabold text-lg tracking-tight leading-none">DATAFORGE <span className="text-cyan-400">AI</span></h1>
              <p className="text-[10px] font-mono text-slate-500 tracking-wider uppercase mt-0.5">Dataset Foundry</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-[#0E1420] px-2.5 py-2"
            data-testid="sidebar-user-chip">
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-[11px] text-slate-300">{user?.email}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-600">{user?.role}</p>
            </div>
            <button data-testid="sidebar-logout-button" onClick={logout} title="Sign out"
              className="text-slate-500 hover:text-rose-400 transition-colors">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testid}
              onClick={() => setTab(n.id)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                tab === n.id
                  ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/30"
                  : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
              )}
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </button>
          ))}
        </nav>

        <div className="px-3 mt-2 flex-1 overflow-y-auto">
          <p className="px-2 text-[10px] font-mono uppercase tracking-widest text-slate-600 mb-2">Crawl Jobs</p>
          {total > 0 && (
            <label className="mb-2 flex items-center gap-2 rounded-md border border-border bg-[#0E1420] px-2.5 py-1.5 focus-within:border-cyan-500/50">
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by ID or name"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                data-testid="crawl-search-input"
                className="w-full min-w-0 bg-transparent font-mono text-[12px] text-slate-200 placeholder:text-slate-600 outline-none"
              />
              {query && (
                <button onClick={() => setQuery("")} aria-label="Clear search" className="text-slate-500 hover:text-slate-200">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </label>
          )}
          <div className="space-y-1" data-testid="crawl-job-list">
            {total === 0 && (
              <p className="px-2 text-xs text-slate-600 font-mono">No jobs yet</p>
            )}
            {total > 0 && crawls.length === 0 && (
              <p className="px-2 text-xs text-slate-600 font-mono" data-testid="crawl-search-empty">No crawl matches "{query}"</p>
            )}
            {crawls.map((c) => (
              <div
                key={c.id}
                data-testid={`crawl-job-${c.id}`}
                onClick={() => onSelect(c.id)}
                className={cn(
                  "group px-2.5 py-2 rounded-md cursor-pointer border transition-colors",
                  activeId === c.id
                    ? "bg-slate-800/70 border-slate-600"
                    : "border-transparent hover:bg-slate-800/40"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium truncate">{c.name}</span>
                  {/* Always visible on touch screens (no hover); hover-only from md up */}
                  <button data-testid={`delete-crawl-${c.id}`} onClick={(e) => onDelete(c, e)} aria-label={`Delete ${c.name}`}
                    className="md:opacity-0 md:group-hover:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase border", STATUS_COLOR[c.status] || STATUS_COLOR.idle)}>
                    {c.status === "running" && <CircleDot className="h-2.5 w-2.5 pulse-dot" />}
                    {c.status}
                  </span>
                  <span className="text-[10px] font-mono text-slate-500">{c.pages_crawled} pgs</span>
                  <CrawlId id={c.id} short className="ml-auto text-[10px] text-slate-500" testid={`crawl-id-${c.id}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AuthGate() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#04060A]" data-testid="auth-loading">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
      </div>
    );
  }
  if (user === false) return <LoginScreen />;
  return <Workspace />;
}
